import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatAED, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, TrendingUp, Clock, Landmark, ReceiptText, Loader2 } from "lucide-react";

function Kpi({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <Card className="p-5 md:p-6 border-slate-200 dark:border-slate-800" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
            {label}
          </div>
          <div className="mt-2 font-mono text-2xl font-semibold text-slate-900 dark:text-slate-50">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null)
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  if (data === false)
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3" data-testid="dashboard-error">
        <p className="text-sm text-slate-500">Couldn't load the dashboard. Your session may have expired.</p>
        <Button onClick={() => window.location.reload()} variant="outline" data-testid="dashboard-retry">Retry</Button>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-sm text-slate-500">Overview for {data.month_label}</p>
        </div>
        {user?.role === "creator" && (
          <Button onClick={() => navigate("/vouchers/new")} className="gap-2" data-testid="new-voucher-btn">
            <Plus className="w-4 h-4" /> New Voucher
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Kpi label="Paid this month" value={formatAED(data.paid_this_month)} sub={`${data.counts?.paid || 0} paid total`}
          icon={TrendingUp} accent="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" testid="kpi-paid" />
        <Kpi label="Pending this month" value={formatAED(data.pending_this_month)} sub={`${data.in_progress || 0} in workflow`}
          icon={Clock} accent="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" testid="kpi-pending" />
        <Kpi label="VAT collected (month)" value={formatAED(data.vat_this_month)} sub="5% UAE VAT on paid"
          icon={Landmark} accent="bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400" testid="kpi-vat" />
        <Kpi label="Total vouchers" value={data.total_vouchers} sub={`${formatAED(data.total_paid_all_time)} paid all-time`}
          icon={ReceiptText} accent="bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400" testid="kpi-total" />
      </div>

      <Card className="border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-semibold text-base">Recent vouchers</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate("/vouchers")} data-testid="view-all-vouchers">
            View all
          </Button>
        </div>
        {(data.recent?.length ?? 0) === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-slate-500">
            No vouchers yet. Create your first payment voucher.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Voucher No</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.recent || []).map((v) => (
                <TableRow
                  key={v.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/vouchers/${v.id}`)}
                  data-testid={`recent-row-${v.voucher_no}`}
                >
                  <TableCell className="font-medium text-sm whitespace-nowrap">{v.company_name || "—"}</TableCell>
                  <TableCell className="font-mono text-sm font-medium">{v.voucher_no}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{v.vendor_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(v.date)}</TableCell>
                  <TableCell className="text-right font-mono">{formatAED(v.grand_total)}</TableCell>
                  <TableCell><StatusBadge status={v.status} locked={v.locked} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
